import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMailAccounts, reorderMailAccounts } from "@/lib/api/accounts";
import { saveMailAccountSettings } from "@/lib/api/accountSettings";
import {
  clearAccountsSessionCache,
  readAccountsSessionCache,
  writeAccountsSessionCache,
} from "@/lib/cache/accountsSessionCache";
import { sortAccountsBySortOrder, applySortOrderToAccounts } from "@/lib/mail/accountSortOrder";
import { migrateLegacyAccountPrefsToServer } from "@/lib/mail/migrateLegacyAccountPrefs";
import { syncAccountSignatures } from "@/lib/mail/syncAccountSignatures";
import type { MailAccountSummary } from "@/lib/types/mail";

import {
  filterAccountsForMailSync,
} from "@/lib/accounts/googleOAuthAccounts";

function sanitizeMailAccounts(list: MailAccountSummary[]): MailAccountSummary[] {
  return filterAccountsForMailSync(list);
}

export function useMailAccounts() {
  const seed = readAccountsSessionCache();
  const initialSeed = seed?.length ? sanitizeMailAccounts(seed) : [];

  const [accounts, setAccounts] = useState<MailAccountSummary[]>(() =>
    initialSeed.length ? sortAccountsBySortOrder(initialSeed) : []
  );
  const [loading, setLoading] = useState(() => initialSeed.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [accountSettingsVersion, setAccountSettingsVersion] = useState(0);

  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const applyAccountOrder = useCallback((orderedIds: number[]) => {
    setAccounts((prev) => {
      const next = applySortOrderToAccounts(prev, orderedIds);
      if (next.length === prev.length) {
        writeAccountsSessionCache(next);
        return next;
      }
      return prev;
    });
  }, []);

  const patchAccount = useCallback((accountId: number, patch: Partial<MailAccountSummary>) => {
    setAccounts((prev) => {
      let next = prev.map((account) => {
        if (account.id !== accountId) {
          if (patch.isDefault === true) {
            return { ...account, isDefault: false };
          }
          return account;
        }

        let updated: MailAccountSummary = { ...account, ...patch };
        if ("displayName" in patch && (patch.displayName == null || patch.displayName === "")) {
          const { displayName: _removed, ...rest } = updated;
          updated = rest as MailAccountSummary;
        }
        return updated;
      });
      writeAccountsSessionCache(next);
      return sortAccountsBySortOrder(next);
    });
  }, []);

  const runBackgroundAccountSync = useCallback(async (list: MailAccountSummary[]) => {
    if (list.length === 0) return;

    try {
      const migrated = await migrateLegacyAccountPrefsToServer(list, {
        reorderMailAccounts: async (accountIds) => {
          const reorderRes = await reorderMailAccounts(accountIds);
          return { ok: reorderRes.ok };
        },
        saveMailAccountSettings: async (accountId, patch) => {
          const saveRes = await saveMailAccountSettings(accountId, patch);
          return { ok: saveRes.ok };
        },
      });
      if (migrated) {
        const refresh = await fetchMailAccounts();
        if (refresh.ok) {
          const refreshed = sortAccountsBySortOrder(refresh.data.accounts ?? []);
          setAccounts(refreshed);
          writeAccountsSessionCache(refreshed);
        }
      }
      const synced = await syncAccountSignatures(
        accountsRef.current.length > 0 ? accountsRef.current : list
      );
      if (synced) {
        setAccountSettingsVersion((v) => v + 1);
      }
    } catch {
      /* migrate/signatures ne doivent jamais bloquer l'UI */
    }
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    const hadAccounts = accountsRef.current.length > 0;

    try {
      const res = await fetchMailAccounts();
      if (!res.ok) {
        setError(res.message);
        if (!hadAccounts) {
          setAccounts([]);
          clearAccountsSessionCache();
        }
        return;
      }

      const list = sanitizeMailAccounts(sortAccountsBySortOrder(res.data.accounts ?? []));
      setAccounts(list);
      if (list.length > 0) {
        writeAccountsSessionCache(list);
      } else {
        clearAccountsSessionCache();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (accounts.length === 0) return;
    const t = window.setTimeout(() => {
      void runBackgroundAccountSync(accountsRef.current);
    }, 4_000);
    return () => window.clearTimeout(t);
  }, [accounts.length, runBackgroundAccountSync]);

  const removeAccount = useCallback((accountId: number) => {
    setAccounts((prev) => {
      const next = sortAccountsBySortOrder(prev.filter((account) => account.id !== accountId));
      if (next.length > 0) {
        writeAccountsSessionCache(next);
      } else {
        clearAccountsSessionCache();
      }
      return next;
    });
  }, []);

  return {
    accounts,
    setAccounts,
    removeAccount,
    applyAccountOrder,
    patchAccount,
    loading,
    error,
    reload,
    hasAccount: accounts.length > 0,
    accountSettingsVersion,
  };
}
